/**
 * 강사 생성 모달 컴포넌트
 * - 이메일, 이름, 전화번호, 비밀번호 입력
 * - 비밀번호 미입력 시 자동 생성
 */

import React, { useState } from 'react';
import { Modal, Form, Input, message } from 'antd';
import { userService, InstructorCreateRequest } from '../../services/userService';

interface CreateInstructorModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateInstructorModal: React.FC<CreateInstructorModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 강사 생성 제출
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: InstructorCreateRequest = {
        email: values.email,
        name: values.name,
        phone: values.phone,
        password: values.password || undefined, // 미입력 시 자동 생성
      };

      await userService.createInstructor(data);
      message.success('강사가 생성되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('강사 생성 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="강사 추가"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="생성"
      cancelText="취소"
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          label="이메일"
          name="email"
          rules={[
            { required: true, message: '이메일을 입력하세요' },
            { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
          ]}
        >
          <Input placeholder="instructor@example.com" />
        </Form.Item>

        <Form.Item
          label="이름"
          name="name"
          rules={[
            { required: true, message: '이름을 입력하세요' },
            { min: 1, max: 100, message: '이름은 1-100자여야 합니다' },
          ]}
        >
          <Input placeholder="홍길동" />
        </Form.Item>

        <Form.Item
          label="전화번호"
          name="phone"
          rules={[
            { pattern: /^\d{3}-\d{4}-\d{4}$/, message: '010-0000-0000 형식으로 입력하세요' },
          ]}
        >
          <Input placeholder="010-0000-0000" />
        </Form.Item>

        <Form.Item
          label="비밀번호 (선택)"
          name="password"
          extra="미입력 시 임시 비밀번호가 자동 생성됩니다"
          rules={[
            { min: 6, message: '비밀번호는 최소 6자 이상이어야 합니다' },
          ]}
        >
          <Input.Password placeholder="최소 6자" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
